import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import { formatValue, getLayerColorProperty } from '../../../utils/mapUtils';
import { cn } from '../../../lib/utils';

const HIDDEN_FEATURE_KEYS = new Set(['h3', 'value', 'geometry', 'id']);

const getLayerIdFromFeature = (feature) =>
  feature.layer.id
    .replace('l-', '')
    .replace('-fill', '')
    .replace('-circle', '')
    .replace('-line', '');

const getMetricLabel = (layer) => {
  if (layer?.query?.valueColumn && layer?.query?.aggregation) {
    return `${layer.query.aggregation.toUpperCase()} (${layer.query.valueColumn})`;
  }
  if (layer?.name) {
    return layer.name.split(' by ')[0];
  }
  return 'Cell Data';
};

const MapFeaturePopupContent = ({ info, layer, isSelected }) => {
  const metricLabel = getMetricLabel(layer);
  const valueProp = getLayerColorProperty(layer) || 'value';
  const extraProperties = Object.entries(info.feature.properties || {}).filter(
    ([key]) => !HIDDEN_FEATURE_KEYS.has(key)
  );

  return (
    <div
      className={cn(
        'pointer-events-none bg-white/95 dark:bg-gray-950/95 backdrop-blur-md p-2 rounded-lg shadow-xl border min-w-[140px]',
        isSelected
          ? 'border-emerald-500/50 dark:border-emerald-400/50 ring-2 ring-emerald-500/10 dark:ring-emerald-400/10'
          : 'border-gray-100/50 dark:border-gray-800/50'
      )}
    >
      <div className="space-y-1.5">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 tracking-[0.15em] leading-none">
              {metricLabel}
            </p>
            {isSelected && (
              <div className="px-1 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 rounded text-[6px] font-black text-emerald-700 dark:text-emerald-300 tracking-tighter">
                Pinned
              </div>
            )}
          </div>
          <p className="text-base font-black text-gray-900 dark:text-white tracking-tight flex items-baseline gap-1">
            {formatValue(info.feature.properties?.[valueProp])}
          </p>
        </div>

        {extraProperties.length > 0 && (
          <>
            <div className="h-px bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800" />
            <div className="flex flex-col gap-1.5 py-0.5">
              {extraProperties.map(([key, val]) => (
                <div key={key} className="flex flex-col">
                  <span className="text-[7px] text-gray-400 dark:text-gray-500 font-bold tracking-wider leading-none mb-0.5">
                    {key.replace(/_/g, ' ')}
                  </span>
                  <span className="text-[10px] text-gray-700 dark:text-gray-200 font-black tracking-tight">
                    {typeof val === 'number' ? formatValue(val) : String(val)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const MapFeaturePopup = ({ hoverInfo, selectedInfo, mapLayers }) => {
  const { current: map } = useMap();
  const popupRef = useRef(null);
  const contentEl = useMemo(() => document.createElement('div'), []);

  const info = selectedInfo || hoverInfo;
  const isSelected = Boolean(selectedInfo);

  useEffect(() => {
    const mapInstance = map?.getMap();
    if (!mapInstance) return undefined;

    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'bottom',
      offset: 12,
      className: 'manteo-map-popup',
      maxWidth: '320px'
    });
    popup.setDOMContent(contentEl);
    popupRef.current = popup;

    return () => {
      popup.remove();
      popupRef.current = null;
    };
  }, [map, contentEl]);

  useEffect(() => {
    const popup = popupRef.current;
    const mapInstance = map?.getMap();
    if (!popup || !mapInstance) return;

    if (!info) {
      if (popup.isOpen()) {
        popup.remove();
      }
      return;
    }

    if (isSelected && info.lngLat) {
      popup.setLngLat(info.lngLat);
    } else {
      popup.trackPointer();
    }

    if (!popup.isOpen()) {
      popup.addTo(mapInstance);
    }
  }, [info, isSelected, map]);

  if (!info) {
    return null;
  }

  const layer = mapLayers.find((entry) => entry.id === getLayerIdFromFeature(info.feature));

  return createPortal(
    <MapFeaturePopupContent info={info} layer={layer} isSelected={isSelected} />,
    contentEl
  );
};

export default MapFeaturePopup;
